import { getUserCommunities, joinCommunity, searchCommunity, leaveCommunity } from "@/common/api/Services/Community";
import { checkIfUnderAge } from "@/common/common";
import { useCallback, useState, useEffect } from "react";
import { ValidationError } from "@/common/api/Exception";
const communityPageDefaultValue = {
    communityId: 0,
    communityName: "",
    localPassword: "",
    invitationCode: "",
    communityValid: false,
    communityPrivate: false
};
const useJoinCommunity = () => {
    const [pageStatus, setPageStatus] = useState("loading");
    const [errorMessages, setErrorMessages] = useState([]);
    const [communityList, setCommunityList] = useState();
    const [communityPage, setCommunityPage] = useState(communityPageDefaultValue);
    const resetCommunityPage = () => {
        setCommunityPage(communityPageDefaultValue);
    };
    const getUsersCommunities = async () => {
        try {
            setCommunityList(await getUserCommunities());
        }
        catch {
            setErrorMessages(['There was an issue loading the communities you belong to, please refresh the page']);
        }
    };
    const setPassword = useCallback((value) => {
        setCommunityPage((prevState) => {
            return {
                ...prevState,
                localPassword: value
            };
        });
    }, []);
    const removeMyselfFromCommunity = useCallback(async (communityId) => {
        try {
            await leaveCommunity(communityId);
            await getUsersCommunities();
        }
        catch (e) {
            const { message = 'Something went wrong, please try again' } = e;
            setPageStatus('error');
            setErrorMessages([message]);
        }
    }, [leaveCommunity, getUsersCommunities]);
    const collectInvitationCode = async (invitationCode) => {
        if (invitationCode.length !== 10) {
            setErrorMessages(['Invitation Codes are 10 characters long']);
            setCommunityPage(() => {
                return { ...communityPageDefaultValue, invitationCode: invitationCode };
            });
            return;
        }
        await validateInvitationCode(invitationCode);
    };
    const populateCommunityPageAfterSearch = (communityId, invitationCode, communityName, communityValid, communityPrivate) => {
        setCommunityPage((prevState) => {
            return {
                ...prevState,
                invitationCode: invitationCode,
                communityId: communityId,
                communityName: communityName,
                communityValid: communityValid,
                communityPrivate: communityPrivate
            };
        });
    };
    const validateInvitationCode = async (invitationCode) => {
        setErrorMessages([]);
        try {
            const response = await searchCommunity(invitationCode);
            if (!response) {
                throw new ValidationError('Something went wrong, please check your invitation code & try again');
            }
            const isPrivate = (response.access === 'private');
            if (!isPrivate && checkIfUnderAge()) {
                throw new ValidationError('You are not able to join a public community when you are under 16');
            }
            populateCommunityPageAfterSearch(parseInt(response.id), invitationCode, response.name, true, isPrivate);
            setPageStatus('success');
        }
        catch (e) {
            const { message = 'Something went wrong, please check your invitation code & try again' } = e;
            setPageStatus('error');
            setErrorMessages([message]);
            populateCommunityPageAfterSearch(0, invitationCode, '', false, false);
        }
    };
    const submitJoinRequest = async () => {
        setErrorMessages([]);
        if (communityPage.communityPrivate && !communityPage.localPassword?.length) {
            setPageStatus('error');
            setErrorMessages(['Password is required']);
            return;
        }
        try {
            await joinCommunity(communityPage.communityId, communityPage.invitationCode, communityPage.localPassword);
            await getUsersCommunities();
            resetCommunityPage();
        }
        catch (e) {
            const { message = 'Something went wrong, please check your invitation code & try again' } = e;
            setPageStatus('error');
            setErrorMessages([message]);
        }
    };
    useEffect(() => {
        getUsersCommunities();
    }, []);
    return {
        setPassword,
        pageStatus,
        communityList,
        errorMessages,
        communityPage,
        submitJoinRequest,
        collectInvitationCode,
        removeMyselfFromCommunity,
    };
};
export { useJoinCommunity };
